import { css, keyframes } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

type Review = { key: 'r1' | 'r2' | 'r3' | 'r4'; date: string; rating: number };

/** 베타 이용자 실제 후기만 싣는다 — 후기를 추가할 땐 원문 의미를 유지한 채 다듬는다 */
const REVIEWS: Review[] = [
  { key: 'r1', date: '2026.10.06', rating: 5 },
  { key: 'r2', date: '2026.10.13', rating: 5 },
  { key: 'r3', date: '2026.09.15', rating: 5 },
  { key: 'r4', date: '2026.10.14', rating: 4 },
];

/** 한 바퀴 분량이 넓은 화면 폭보다 길어야 이음매가 보이지 않는다 */
const LOOP_REPEAT = 2;
/** 두 줄이 같은 속도로 겹쳐 보이지 않도록 둘째 줄은 반대 방향·다른 속도 */
const ROW_MOTION = [
  { seconds: 70, direction: 'normal' },
  { seconds: 80, direction: 'reverse' },
] as const;
/** 후기가 적을 땐 둘째 줄을 순서만 바꿔 채운다 — 같은 후기가 위아래로 나란히 서지 않게 */
const MIN_REVIEWS_TO_SPLIT = 6;

const ROWS: Review[][] =
  REVIEWS.length >= MIN_REVIEWS_TO_SPLIT
    ? [REVIEWS.filter((_, i) => i % 2 === 0), REVIEWS.filter((_, i) => i % 2 === 1)]
    : [[...REVIEWS], [...REVIEWS.slice(1), REVIEWS[0]]];
const SECOND_ROW_IS_COPY = REVIEWS.length < MIN_REVIEWS_TO_SPLIT;

const marquee = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(-50%)' },
});

const MAX_RATING = 5;
const STAR_EMPTY_COLOR = '#DDE1E6';

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    aria-hidden
    style={filled ? undefined : { color: STAR_EMPTY_COLOR }}
  >
    <path
      fill="currentColor"
      d="M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95L12 2.5z"
    />
  </svg>
);

export const Reviews = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-review-title">
      <Box css={styles.head}>
        <SectionHead id="ai-review-title" titleKey="ai_interviews.landing.review.title" />
      </Box>

      <Box css={styles.band}>
        {ROWS.map((row, rowIndex) => {
          const loop = Array.from({ length: LOOP_REPEAT * 2 }, () => row).flat();
          const motion = ROW_MOTION[rowIndex];
          return (
            <Box
              key={motion.seconds}
              component="ul"
              css={styles.track}
              style={{
                animationDuration: `${motion.seconds}s`,
                animationDirection: motion.direction,
              }}
              aria-hidden={rowIndex > 0 && SECOND_ROW_IS_COPY ? true : undefined}
            >
              {loop.map((review, index) => (
                <Box
                  key={`${review.key}-${index}`}
                  component="li"
                  css={styles.card}
                  aria-hidden={index >= row.length ? true : undefined}
                >
                  <Box
                    css={styles.stars}
                    aria-label={t('ai_interviews.landing.review.stars_aria', {
                      rating: review.rating,
                    })}
                    role="img"
                  >
                    {Array.from({ length: MAX_RATING }, (_, star) => (
                      <StarIcon key={star} filled={star < review.rating} />
                    ))}
                  </Box>
                  <Text css={styles.body}>{t(`ai_interviews.landing.review.${review.key}`)}</Text>
                  <Text component="span" css={styles.date}>
                    {review.date}
                  </Text>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    overflow: 'hidden',
    background: '#fff',
    padding: `${LANDING_SPACE.sectionY} 0 calc(${LANDING_SPACE.sectionY} + 32px)`,
  }),
  head: css({
    maxWidth: 1200,
    margin: '0 auto',
    padding: `0 ${LANDING_SPACE.pageX}`,
  }),
  band: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: LANDING_SPACE.blockTop,
    '&:hover ul': {
      animationPlayState: 'paused',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& > ul[aria-hidden]': {
        display: 'none',
      },
    },
  }),
  track: css({
    display: 'flex',
    gap: 16,
    width: 'max-content',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    animation: `${marquee} ${ROW_MOTION[0].seconds}s linear infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      width: 'auto',
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: `0 ${LANDING_SPACE.pageX}`,
      '& > li[aria-hidden]': {
        display: 'none',
      },
    },
  }),
  card: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    width: 340,
    padding: 26,
    borderRadius: 24,
    background: LANDING_COLOR.surface,
    [LANDING_MOBILE_MQ]: {
      width: 280,
      padding: 22,
    },
  }),
  stars: css({
    display: 'flex',
    gap: 1,
    color: '#FAB005',
  }),
  body: css({
    flex: 1,
    color: LANDING_COLOR.ink,
    fontSize: LANDING_FONT.body,
    fontWeight: 600,
    lineHeight: 1.6,
    wordBreak: 'keep-all',
  }),
  date: css({
    color: LANDING_COLOR.subtle,
    fontSize: LANDING_FONT.label,
  }),
};
